import { Component, h, State, Event, EventEmitter, Prop, Element } from '@stencil/core';

@Component({
  tag: 'biel-input',
  styleUrl: 'biel-input.css',
  shadow: true,
})
export class BielInput {
  @State() userInput: string = '';
  @Event() messageSent: EventEmitter<{ bielId: string; message: string }>;
  @Event() scrollToBottom: EventEmitter<{ bielId: string }>;
  @Event() thinkModeChanged: EventEmitter<{ bielId: string; mode: 'auto' | 'fast' | 'think' }>;

  @Prop() placeholderText: string = 'Type your message';
  @Prop() sendButtonText: string = 'Send';
  @Prop() isDisabled: boolean = false;
  @Prop() bielId: string;
  @Prop() project: string = '';
  @Prop() mcpEnabled: boolean = false;
  @Prop() mcpServerName: string = '';
  @Prop() mcpMetadataTag: string = '';
  @Prop() mcpServerUrl: string = '';
  @Prop() apiKey: string = '';
  @Prop() hideSettingsButton: boolean = false;
  @Prop() hideConnectButton: boolean = false;
  @Prop() currentThinkMode: 'auto' | 'fast' | 'think' = 'auto';
  @Prop() connectButtonText: string = 'Connect';
  @Prop() thinkModeAutoText: string = 'Auto';
  @Prop() thinkModeFastText: string = 'Instant';
  @Prop() thinkModeThinkText: string = 'Think';
  @Prop() thinkModeAutoDescription: string = 'Balances speed and quality';
  @Prop() thinkModeFastDescription: string = 'Prioritizes speed';
  @Prop() thinkModeThinkDescription: string = 'Takes longer for better accuracy';
  @Prop() mcpUrlText: string = 'MCP URL';
  @Prop() mcpUrlDescription: string = 'Copy to clipboard';
  @Prop() mcpClaudeText: string = 'Claude Code';
  @Prop() mcpClaudeDescription: string = 'CLI command';
  @Prop() mcpCopilotText: string = 'Copilot (VS code)';
  @Prop() mcpCopilotDescription: string = 'Opens in a new tab';
  @Prop() mcpCursorText: string = 'Cursor';
  @Prop() mcpCursorDescription: string = 'Opens in a new tab';
  @Prop() mcpCopiedText: string = 'Copied!';
  @Prop() mcpUrlCopiedDescription: string = 'URL copied to clipboard';
  @Prop() mcpClaudeCopiedDescription: string = 'Command copied to clipboard';

  @Element() el: HTMLElement;

  componentDidLoad() {
    const textarea = this.el.shadowRoot.querySelector('textarea');
    if (textarea) {
      textarea.focus();
    }
  }

  handleInputChange(event) {
    this.userInput = event.target.value;
    this.adjustTextareaHeight();
  }

  handleSendMessage() {
    if (this.userInput.trim()) {
      this.messageSent.emit({ bielId: this.bielId, message: this.userInput });
      this.userInput = '';
      this.resetTextareaHeight();
      this.scrollToBottom.emit({ bielId: this.bielId });
    }
  }

  handleKeyPress(event) {
    if (event.key === 'Enter' && !event.shiftKey && !this.isDisabled) {
      event.preventDefault();
      this.handleSendMessage();
    }
  }

  getCSSVariable(element: HTMLElement, variableName: string): string {
    return getComputedStyle(element).getPropertyValue(variableName);
  }

  getPixelValue(element: HTMLElement, variableName: string): number {
    const value = this.getCSSVariable(element, variableName);
    const numValue = parseFloat(value.replace('px', ''));
    return Math.round(numValue);
  }

  adjustTextareaHeight() {
    const textarea = this.el.shadowRoot.querySelector('textarea');
    const minHeight = this.getCSSVariable(textarea, 'min-height');
    const maxHeight = this.getPixelValue(textarea, 'max-height');
    const padding = this.getPixelValue(textarea, 'padding') * 2;

    textarea.style.height = minHeight;
    const newHeight = Math.min(textarea.scrollHeight - padding, maxHeight);
    textarea.style.height = `${Math.round(newHeight)}px`;
    textarea.style.overflowY = newHeight >= maxHeight ? 'visible' : 'hidden';
  }

  resetTextareaHeight() {
    const textarea = this.el.shadowRoot.querySelector('textarea');
    const minHeight = this.getPixelValue(textarea, 'min-height');
    textarea.style.height = `${minHeight}px`;
    textarea.style.overflowY = 'hidden';
  }

  componentDidUpdate() {
    const textarea = this.el.shadowRoot.querySelector('textarea');
    if (!this.isDisabled) {
      textarea.focus();
    }
  }

  render() {
    return (
      <div class="biel-input-wrapper" part="wrapper">
        <biel-input-options
          project={this.project}
          mcpEnabled={this.mcpEnabled}
          mcpServerName={this.mcpServerName}
          mcpMetadataTag={this.mcpMetadataTag}
          mcpServerUrl={this.mcpServerUrl}
          apiKey={this.apiKey}
          hideSettingsButton={this.hideSettingsButton}
          hideConnectButton={this.hideConnectButton}
          currentThinkMode={this.currentThinkMode}
          bielId={this.bielId}
          connectButtonText={this.connectButtonText}
          thinkModeAutoText={this.thinkModeAutoText}
          thinkModeFastText={this.thinkModeFastText}
          thinkModeThinkText={this.thinkModeThinkText}
          thinkModeAutoDescription={this.thinkModeAutoDescription}
          thinkModeFastDescription={this.thinkModeFastDescription}
          thinkModeThinkDescription={this.thinkModeThinkDescription}
          mcpUrlText={this.mcpUrlText}
          mcpUrlDescription={this.mcpUrlDescription}
          mcpClaudeText={this.mcpClaudeText}
          mcpClaudeDescription={this.mcpClaudeDescription}
          mcpCopilotText={this.mcpCopilotText}
          mcpCopilotDescription={this.mcpCopilotDescription}
          mcpCursorText={this.mcpCursorText}
          mcpCursorDescription={this.mcpCursorDescription}
          mcpCopiedText={this.mcpCopiedText}
          mcpUrlCopiedDescription={this.mcpUrlCopiedDescription}
          mcpClaudeCopiedDescription={this.mcpClaudeCopiedDescription}
          onThinkModeChanged={(e) => this.thinkModeChanged.emit(e.detail)}
          exportparts="container: biel-input-options-container, button: biel-input-options-button, dropdown: biel-input-options-dropdown, item: biel-input-options-item, success: biel-input-options-success, settings-button: biel-input-options-settings-button, settings-dropdown: biel-input-options-settings-dropdown"
        ></biel-input-options>
        <div class="biel-input-container" part="container">
          <textarea
            class="biel-input__textarea"
            part="textarea"
            placeholder={this.placeholderText}
            value={this.userInput}
            onInput={(event) => this.handleInputChange(event)}
            onKeyPress={(event) => this.handleKeyPress(event)}
            disabled={this.isDisabled}
          ></textarea>
          <button
            class="biel-input__button"
            part="send-button"
            onClick={() => this.handleSendMessage()}
            disabled={this.isDisabled}
          >
            {this.sendButtonText}
          </button>
        </div>
      </div>
    );
  }
}
